/**
 * THE TREND, DRAWN — the small presentational pieces the roll-up cell and the
 * project report share, so the arrow and the history strip read the same
 * wherever they appear. All the arithmetic is in src/portfolio-trend.ts; these
 * only render what it derived, to the approved mocks (dev-preview/rag-trend).
 */
import type { Trend, StripView, TrendDir } from '../../portfolio-trend';
import { RAG_WORD } from '../../portfolio-trend';

/** The three trend glyphs, value-for-value with the mock: a rising line, a
 *  falling line, two level lines. Sentiment is carried by shape AND word AND
 *  colour together, so the read survives without colour. */
const ARROW: Record<TrendDir, JSX.Element> = {
  up: <><path d="M5 17L17 5" /><path d="M9 5h8v8" /></>,
  down: <><path d="M5 7L17 19" /><path d="M9 19h8v-8" /></>,
  flat: <><path d="M5 10h14" /><path d="M5 14h14" /></>,
};

function Mark({ dir }: { dir: TrendDir }) {
  return (
    <span className="tmark">
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {ARROW[dir]}
      </svg>
    </span>
  );
}

/** The word a trend line reads: the grade it moved from, or how long it has held.
 *  A held grade reads "stuck · N wks" only when it is amber or red — a steadily
 *  On track project is "steady", never "stuck" (stuck names trouble that persists). */
function trendLabel(trend: Extract<Trend, { kind: 'trend' }>): string {
  if (trend.dir === 'flat') {
    return trend.to !== 'green' && trend.stuckWeeks >= 2 ? `stuck · ${trend.stuckWeeks} wks` : 'steady';
  }
  return `from ${RAG_WORD[trend.from]}`;
}

/**
 * THE ROLL-UP TREND LINE — one quiet line under the health pill: an arrow and a
 * word for "better or worse than last week", or a structural "New" tag on a
 * project's first pulse. Nothing while parked or before the first pulse — the
 * cell then carries only its pill.
 */
export function RagTrend({ trend }: { trend: Trend }) {
  if (trend.kind === 'new') return <span className="rag-new">New · first snapshot</span>;
  if (trend.kind !== 'trend') return null;
  return (
    <span className={`rag-trend ${trend.dir}`} title={trendLabel(trend)}>
      <Mark dir={trend.dir} />
      {trendLabel(trend)}
    </span>
  );
}

function cellClass(cls: StripView['cells'][number]['cls'], now: boolean): string {
  return `cell${cls === 'none' ? ' none' : ` ${cls}`}${now ? ' now' : ''}`;
}

/**
 * THE REPORT TREND BLOCK — the roomy home for the history: an 8-pulse strip with
 * a dated axis, the last-change arrow, and a plain sentence that does the
 * arithmetic. Renders only when at least one pulse has been recorded.
 */
export function ReportTrend({ view, periods = 8 }: { view: StripView; periods?: number }) {
  if (view.filled === 0) return null;
  return (
    <div className="pf-trend-block">
      <div className="pf-trend-head">
        Trend — last {periods} weekly snapshots <RagTrend trend={view.trend} />
      </div>
      <span className="rag-strip lg">
        {view.cells.map((c, i) => (
          <span key={i} className={cellClass(c.cls, c.now)} />
        ))}
      </span>
      <div className="pf-trend-axis">
        {view.axis.map((a, i) => (
          <span key={i}>{a}</span>
        ))}
      </div>
      {view.say ? <div className="pf-trend-say">{view.say}</div> : null}
    </div>
  );
}
